import React, {
  ReactNode,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useKeycloak } from '@react-keycloak/web';
import Keycloak, { KeycloakPromise } from 'keycloak-js';
import { useLocalStorage, usePrevious } from 'react-use';
import Cookies from 'js-cookie';

import AuthContext, {
  AuthContract,
  PermissionListType,
} from '../contexts/AuthContext';
import { useAppLoading } from '#core/AppLoading';
import CommonPermissions from '#core/Permissions/components/CommonPermissions';
import { useEnvironment } from '#core/Environment';
import getPermissionToken from '#utils/getPermissionToken';
import getPermissionList from '#utils/getPermissionList';
import { parseJwt } from '#utils/parseJwt';

type ParsedTokenWithExtraFields =
  | (Keycloak.KeycloakTokenParsed & {
      preferred_username?: string;
      groups?: [string];
      given_name: string;
      family_name: string;
      name: string;
      email?: string;
    })
  | undefined;

type AuthProviderProps = {
  children: (data: AuthContract) => ReactNode | null;
};

const AuthProvider: React.FC<AuthProviderProps> = ({
  children: renderChildren,
}) => {
  const { keycloak } = useKeycloak();
  const { token: keycloakToken } = keycloak || {};
  const { apiEndpoint } = useEnvironment();
  const { setIsLoading } = useAppLoading();
  const authenticated = keycloak?.authenticated || false;
  const tokenParsed = keycloak?.tokenParsed as ParsedTokenWithExtraFields;
  const groups = useMemo(() => tokenParsed?.groups || ([] as string[]), [
    tokenParsed?.groups,
  ]);
  const roles = useMemo(
    () => tokenParsed?.realm_access?.roles || ([] as string[]),
    [tokenParsed?.realm_access?.roles],
  );
  const canUseElevatedPermissions = roles.includes('AssumeAllInstitutions');

  const [useElevatedPermissions, setUseElevatedPermissions] = useLocalStorage<
    boolean
  >(`${tokenParsed?.sub}-use-elevated-permissions`, canUseElevatedPermissions);
  const previousUseElevatedPermisisons = usePrevious(useElevatedPermissions);

  const [permissionToken, setPermissionToken] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [permissionList, setPermissionList] = useState<
    PermissionListType[] | null
  >(null);

  const fetchPermissionsToken = useCallback(async () => {
    if (!keycloakToken) {
      return;
    }

    const permissionsToken = await getPermissionToken(
      apiEndpoint,
      keycloakToken,
    );
    const permissionsTokenParsed = parseJwt(permissionsToken);
    setPermissionToken(permissionsToken);
    setIsAdmin(permissionsTokenParsed?.isAdmin);
  }, [apiEndpoint, keycloakToken]);

  const fetchPermissionsList = useCallback(async () => {
    if (!keycloakToken) {
      return;
    }

    const list = await getPermissionList(apiEndpoint, keycloakToken);
    setPermissionList(list);
  }, [keycloakToken, apiEndpoint]);

  useEffect(() => {
    fetchPermissionsToken().then(fetchPermissionsList);
  }, [fetchPermissionsList, fetchPermissionsToken]);

  useEffect(() => {
    if (useElevatedPermissions !== previousUseElevatedPermisisons) {
      setIsLoading(true);
    }
  }, [useElevatedPermissions, previousUseElevatedPermisisons, setIsLoading]);

  const authContextValue: AuthContract = useMemo(() => {
    const token = keycloak?.token || '';
    return {
      keycloak,
      isAuthenticated: authenticated,
      token,
      isAdmin,
      user: tokenParsed
        ? {
            id: tokenParsed.sub as string,
            username: tokenParsed.preferred_username as string,
            givenName: tokenParsed.given_name,
            familyName: tokenParsed.family_name,
            name: tokenParsed.name,
            email: tokenParsed?.email,
          }
        : null,
      roles,
      groups,
      actions: {
        logIn:
          keycloak?.login ||
          (() => Promise.resolve() as KeycloakPromise<void, void>),
        logOut: (): Keycloak.KeycloakPromise<void, void> => {
          const fn =
            keycloak?.logout ||
            (() => Promise.resolve() as KeycloakPromise<void, void>);
          sessionStorage.clear();

          Cookies.remove('selectedInstitutionId', {
            path: '/',
          });
          return fn();
        },
      },
      canUseElevatedPermissions,
      useElevatedPermissions:
        canUseElevatedPermissions && useElevatedPermissions === true,
      setUseElevatedPermissions: canUseElevatedPermissions
        ? setUseElevatedPermissions
        : () => {},
      apiHeaders: { authorization: `Bearer ${token}` },
      permissionToken,
      permissionList,
      setPermissionToken,
    };
  }, [
    isAdmin,
    authenticated,
    canUseElevatedPermissions,
    groups,
    keycloak,
    roles,
    setUseElevatedPermissions,
    tokenParsed,
    useElevatedPermissions,
    permissionToken,
    permissionList,
  ]);

  if (
    typeof keycloak === 'undefined' ||
    typeof keycloak.authenticated === 'undefined'
  ) {
    // keycloak is not initialised
    return null;
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      {/* setup the keycloak related permissions (e.g. hasRole and hasGroup) */}
      <CommonPermissions resourcePermissions={{}}>
        {renderChildren(authContextValue)}
      </CommonPermissions>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
